import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';

import {
  AdminFormActionTypes,
  AdminFormConnectionsLoad,
  AdminFormConnectionsLoadFail,
  AdminFormConnectionsLoadSuccess,
  AdminFormLoad,
  AdminFormLoadFail,
  AdminFormLoadSuccess,
  AdminFormInfoLoad,
  AdminFormInfoLoadFail,
  AdminFormInfoLoadSuccess,
  AdminFormsListLoad,
  AdminFormsListLoadSuccess,
  AdminFormsListLoadFail,
  AdminFormCreate,
  AdminFormCreateSuccess,
  AdminFormUpdate,
  AdminFormUpdateSuccess,
  AdminFormUpdateStatus,
  AdminFormUpdateStatusSuccess,
  AdminFormDelete,
  AdminFormDeleteSuccess,
  AdminFormRemoveConnection,
  AdminFormRemoveConnectionSuccess,
  AdminFormAddConnectionsSuccess,
  AdminFormAddConnections,
} from '@completion/actions';
import {
  AdminFormDto,
  AdminFormInfo,
  AdminFormListItemDto,
  AdminProjectDto,
  FormConnectionDto,
  PaginatedResponse,
} from '@completion/models';
import { State } from '@completion/reducers';
import { getAdminProjects } from '@completion/selectors';
import { NotificationService } from '@completion/services';
import { trackResource } from './resource-fetch.util';
import { AdminFormConnectionError, AdminFormService } from '../../services/admin-form.service';

@Injectable()
export class AdminFormEffect {
  constructor(
    private actions$: Actions,
    private adminFormService: AdminFormService,
    private store: Store<State>,
    private toastr: NotificationService,
  ) {}

  loadConnections$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormConnectionsLoad>(AdminFormActionTypes.AdminFormConnectionsLoad),
      trackResource<FormConnectionDto[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'getConnections',
        serviceArgs: (action) => [action.formGuid],
        onSuccess: (response) => new AdminFormConnectionsLoadSuccess(response),
        onError: (error) => new AdminFormConnectionsLoadFail(error),
      }),
    ),
  );

  loadAdminForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormLoad>(AdminFormActionTypes.AdminFormLoad),
      trackResource<AdminFormDto>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'getAdminForm',
        serviceArgs: (action) => [action.guid],
        onSuccess: (response) => new AdminFormLoadSuccess(response),
        onError: (error) => new AdminFormLoadFail(error),
      }),
    ),
  );

  loadAdminFormInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormInfoLoad>(AdminFormActionTypes.AdminFormInfoLoad),
      trackResource<AdminFormInfo>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'getAdminFormInfo',
        serviceArgs: (action) => [action.formGuid],
        onSuccess: (formInfo) => new AdminFormInfoLoadSuccess(formInfo),
        onError: (error) => new AdminFormInfoLoadFail(error),
      }),
    ),
  );

  loadAdminForms$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormsListLoad>(AdminFormActionTypes.AdminFormsListLoad),
      trackResource<PaginatedResponse<AdminFormListItemDto>>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'getAdminForms',
        serviceArgs: (action) => [action.params],
        onSuccess: (response) => new AdminFormsListLoadSuccess(response),
        onError: (error) => new AdminFormsListLoadFail(error),
      }),
    ),
  );

  createAdminForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormCreate>(AdminFormActionTypes.AdminFormCreate),
      trackResource<AdminFormDto>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'createAdminForm',
        serviceArgs: (action) => [action.form],
        onSuccess: (response) => new AdminFormCreateSuccess(response),
      }),
    ),
  );

  updateAdminForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormUpdate>(AdminFormActionTypes.AdminFormUpdate),
      trackResource<AdminFormDto>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'updateAdminForm',
        serviceArgs: (action) => [action.form],
        onSuccess: (response) => new AdminFormUpdateSuccess(response),
      }),
    ),
  );

  updateAdminFormStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormUpdateStatus>(AdminFormActionTypes.AdminFormUpdateStatus),
      trackResource<AdminFormDto>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'updateAdminFormStatus',
        serviceArgs: (action) => [action.guid, action.status],
        onSuccess: (response) => new AdminFormUpdateStatusSuccess(response),
      }),
    ),
  );

  deleteAdminForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormDelete>(AdminFormActionTypes.AdminFormDelete),
      trackResource<void>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'deleteAdminForm',
        serviceArgs: (action) => [action.form.guid],
        onSuccess: () => new AdminFormDeleteSuccess(),
      }),
    ),
  );

  addConnections$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormAddConnections>(AdminFormActionTypes.AdminFormAddConnections),
      withLatestFrom(this.store.select(getAdminProjects)),
      trackResource<AdminFormDto>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'addConnections',
        serviceArgs: ([action]) => [action.request],
        onSuccess: (response) => new AdminFormAddConnectionsSuccess(response),
        errorMessage: (error, sourceArgs) => {
          const [, projects] = sourceArgs?.[0] as [AdminFormAddConnections, AdminProjectDto[]];

          // If the error is an AdminFormConnectionError, extract the failed project names and their corresponding error messages
          const errorForProjects = error instanceof AdminFormConnectionError ? error.failedProjects : [];
          const errorMessages = errorForProjects.map((failedProject) => {
            const projectName = projects.find((project) => project.id === failedProject.projectId)?.name ?? String(failedProject.projectId);
            const errorMessage = failedProject.error?.error?.message ?? 'Unknown error';
            return `'${projectName}': ${errorMessage}`;
          });

          return errorMessages.length > 0
            ? `Failed to add checksheet connections:\n\n ${errorMessages.join('\n\n')}`
            : 'Failed to add checksheet connections.';
        },
        onError: (_error, sourceArgs) => {
          const [action] = sourceArgs?.[0] as [AdminFormAddConnections, AdminProjectDto[]];
          return new AdminFormConnectionsLoad(action.request.formGuid);
        },
      }),
    ),
  );

  removeConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormRemoveConnection>(AdminFormActionTypes.AdminFormRemoveConnection),
      trackResource<void>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'removeConnection',
        serviceArgs: (action) => [action.formGuid, action.connection],
        onSuccess: (_response, sourceArgs) => new AdminFormRemoveConnectionSuccess((sourceArgs?.[0] as AdminFormRemoveConnection).formGuid),
      }),
    ),
  );

  reloadConnectionsAfterAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormAddConnectionsSuccess>(AdminFormActionTypes.AdminFormAddConnectionsSuccess),
      map((action) => new AdminFormConnectionsLoad(action.form.guid)),
    ),
  );

  reloadConnectionsAfterRemove$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormRemoveConnectionSuccess>(AdminFormActionTypes.AdminFormRemoveConnectionSuccess),
      map((action) => new AdminFormConnectionsLoad(action.formGuid)),
    ),
  );
}
